/**
 * ═══════════════════════════════════════════════════════════════
 * PRICING DISPLAY UTILITIES
 * ═══════════════════════════════════════════════════════════════
 * 
 * Determines which pricing tabs to show based on user context
 */

/**
 * Get pricing context based on current path
 * 
 * @param {string} pathname - Current route pathname
 * @returns {'tse' | 'ats'} - Which product context we're in
 */
export const getPricingContext = (pathname) => {
  // TSE-related pages show TSE + Bundle
  if (pathname.includes('/sourcing') || 
      pathname.includes('/talent-search') ||
      pathname.includes('/outreach') ||
      pathname.includes('/sourcing/profile')) {
    return 'tse';
  }
  
  // All other pages show ATS + Bundle (default)
  return 'ats';
};

/**
 * Get available plan types for current context
 * 
 * @param {string} pathname - Current route pathname
 * @returns {string[]} - Array of plan types to display ['ats', 'bundle'] or ['tse', 'bundle']
 */
export const getAvailablePlanTypes = (pathname) => {
  const context = getPricingContext(pathname);
  
  if (context === 'tse') {
    return ['tse', 'bundle'];
  }
  
  return ['ats', 'bundle'];
};

/**
 * Get default active tab for pricing display
 * 
 * @param {string} pathname - Current route pathname
 * @returns {string} - Default tab to show
 */
export const getDefaultPricingTab = (pathname) => {
  const context = getPricingContext(pathname);
  return context; // 'tse' or 'ats'
};

/**
 * Check if current page is in TSE context
 * 
 * @param {string} pathname - Current route pathname
 * @returns {boolean}
 */
export const isTSEContext = (pathname) => {
  return getPricingContext(pathname) === 'tse';
};

/**
 * Check if current page is in ATS context
 * 
 * @param {string} pathname - Current route pathname
 * @returns {boolean}
 */
export const isATSContext = (pathname) => {
  return getPricingContext(pathname) === 'ats';
};

/**
 * Get context-aware pricing description
 * 
 * @param {string} pathname - Current route pathname
 * @returns {string} - Description text for the context
 */
export const getContextDescription = (pathname, t) => {
  const context = getPricingContext(pathname);
  
  if (context === 'tse') {
    if (t) {
      return t('contextDescription.tse');
    }
    return 'Unlock full access to Talent Search Engine or get the bundle for best value.';
  }
  
  if (t) {
    return t('contextDescription.ats');
  }
  return 'Power your hiring with our ATS or get the bundle to include Talent Search Engine.';
};

export default {
  getPricingContext,
  getAvailablePlanTypes,
  getDefaultPricingTab,
  isTSEContext,
  isATSContext,
  getContextDescription,
};
